import { faCaretRight, faPaperclip } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AvForm } from "availity-reactstrap-validation";
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { Button } from "reactstrap";
import { createNewMessage, createSentMessage } from "../helpers/utils";
import {
  deleteFile,
  resetFileUpload,
  showTypingIndicator,
  uploadFile,
} from "../redux/chat/actions";
import { addNewMessageSuccessInChatList } from "../redux/rootActions";
import { writeMessage } from "../redux/socket/actions";
import { TranslationComponentProps } from "../types/translationComponentProps";
import { IFileAttachment } from "../types/webchatConversation";

interface IProps extends TranslationComponentProps {
  disabled: boolean;
  identity: any;
}

const ChatForm: React.FC<IProps> = (props: IProps) => {
  const [inputField, setInputField] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // Redux state
  const { uploadedFilePath, isUploading, isDeleting, error } = useSelector(
    (state: RootStateOrAny) => state.fileUpload,
  );
  const dispatch = useDispatch();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previousUploadedFilePath = useRef<string | null>(null);

  const user = useSelector((state: RootStateOrAny) => state.Auth.user);

  // Detekcija mobile uređaja
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Osiguraj početnu visinu textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "40px";
    }
  }, []);

  // Reset selectedFile kada se fajl uspešno obriše sa servera
  useEffect(() => {
    // Ako je prethodno postojao uploadedFilePath, a sada ne postoji i nije se brisao
    if (
      previousUploadedFilePath.current &&
      !uploadedFilePath &&
      !isDeleting &&
      selectedFile
    ) {
      setSelectedFile(null);
      setFileError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }

    // Ažuriraj prethodno stanje
    previousUploadedFilePath.current = uploadedFilePath;
  }, [uploadedFilePath, isDeleting, selectedFile]);

  const handleChange = (event) => {
    const target = event.target;

    // Ne reaguj na file input
    if (target.type === "file") {
      return;
    }

    // Auto-grow do maxHeight, bez uticaja na parent
    target.style.height = "40px"; // počinje sa 1 redom

    const maxHeight = 120; // maksimalno 4 reda
    const newHeight = Math.min(target.scrollHeight, maxHeight);

    target.style.height = `${newHeight}px`;

    // Dinamički ažuriraj visinu chat forme - SAMO ZA DESKTOP
    if (!isMobile) {
      const chatForm = document.querySelector(".chat-form") as HTMLElement;
      if (chatForm) {
        const baseHeight = 40; // početna visina textarea
        const textareaHeight = newHeight;

        // Samo ako se textarea stvarno povećala
        if (textareaHeight > baseHeight) {
          const extraHeight = textareaHeight - baseHeight;
          chatForm.style.minHeight = `${80 + extraHeight}px`; // povećava chat form
        }
      }
    }

    setInputField(target.value);
  };

  const handleMessageSubmit = () => {
    if (!inputField.trim() && !uploadedFilePath) return;

    // Kreiraj file attachment ako postoji uploadovan fajl
    let fileAttachment: IFileAttachment | undefined = undefined;
    if (uploadedFilePath && selectedFile) {
      fileAttachment = {
        url: uploadedFilePath,
        fileName: selectedFile.name,
        size: selectedFile.size,
        mimeType: selectedFile.type,
        thumbnail: undefined, // WCB aplikacija će dodati thumbnail
      };
    }

    // Kreiraj poruku sa čistim tekstom i file attachment-om
    const messageText = inputField.trim() || "";
    const newMsgForWCB = createNewMessage(messageText, fileAttachment);
    const sentMsg = createSentMessage(
      messageText,
      user.email,
      user.avatar,
      newMsgForWCB.type,
    );

    setInputField("");
    // Reset file upload state lokalno (bez brisanja sa servera)
    setSelectedFile(null);
    dispatch(resetFileUpload());
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Reset visine textarea na početnu vrednost
    if (textareaRef.current) {
      textareaRef.current.style.height = "40px"; // fiksna početna visina
      textareaRef.current.scrollTop = 0; // reset scroll pozicije
    }

    // Reset visine chat forme - SAMO ZA DESKTOP
    if (!isMobile) {
      const chatForm = document.querySelector(".chat-form") as HTMLElement;
      if (chatForm) {
        chatForm.style.minHeight = "80px"; // reset na default visinu
      }
    }

    dispatch(addNewMessageSuccessInChatList(sentMsg));
    dispatch(writeMessage(newMsgForWCB));

    // Prikaži typing indicator (da neko kuca sa druge strane)
    dispatch(showTypingIndicator());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault(); // sprečava novi red
      handleMessageSubmit();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Provera veličine fajla (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setFileError(props.t("CHAT_FORM.file_too_large"));
        setTimeout(() => setFileError(null), 5000);
        return;
      }

      // Provera tipa fajla
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
      ];

      if (!allowedTypes.includes(file.type)) {
        setFileError(props.t("CHAT_FORM.file_type_not_supported"));
        setTimeout(() => setFileError(null), 5000);
        return;
      }

      // Ukloni error ako postoji
      setFileError(null);

      // Sprečavaj da se putanja fajla doda u textarea
      event.preventDefault();
      event.stopPropagation();

      // Eksplicitno očisti textarea - zadrži postojeći tekst
      setInputField(inputField);

      // Odmah pokreni upload
      dispatch(uploadFile(file));
      setSelectedFile(file);

      // Resetuj input fajl da ne ostane putanja
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeUploadedFile = () => {
    if (uploadedFilePath) {
      dispatch(deleteFile(uploadedFilePath));
    }
  };

  return (
    <>
      <div className="chat-form">
        {/* Error poruka */}
        {fileError && (
          <div className="file-error-message">
            <span className="error-text">{fileError}</span>
          </div>
        )}

        {/* Dinamički prikaz fajla */}
        {(selectedFile || uploadedFilePath) && (
          <div
            className={`file-container ${
              uploadedFilePath ? "uploaded" : "uploading"
            }`}
          >
            <div className="file-info">
              <FontAwesomeIcon icon={faPaperclip} className="file-icon" />
              <span className="file-name">{selectedFile?.name || "Fajl"}</span>
              <span className="file-size">
                (
                {selectedFile
                  ? (selectedFile.size / 1024 / 1024).toFixed(2)
                  : "0.00"}{" "}
                MB)
              </span>
            </div>
            <div className="file-actions">
              {isUploading && (
                <span className="uploading-text">
                  {props.t("CHAT_FORM.uploading")}
                </span>
              )}
              <Button
                size="sm"
                className={`file-action-button ${
                  uploadedFilePath ? "delete-server" : "remove-local"
                }`}
                onClick={
                  uploadedFilePath ? removeUploadedFile : removeSelectedFile
                }
                disabled={isUploading || isDeleting}
              >
                {isDeleting
                  ? props.t("CHAT_FORM.deleting")
                  : uploadedFilePath
                  ? props.t("CHAT_FORM.delete_from_server")
                  : props.t("CHAT_FORM.remove")}
              </Button>
            </div>
          </div>
        )}

        <AvForm
          className="d-inline-flex w-100 chat-form-group"
          onValidSubmit={handleMessageSubmit}
          onChange={handleChange}
        >
          <textarea
            ref={textareaRef}
            className="chat-form-input"
            placeholder={props.t("CHAT_FORM.type_message")}
            name="text"
            value={inputField}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={props.disabled}
            rows={1}
          />

          {/* Desktop dugme za file upload - sa plavim okvirom i resize */}
          {!isMobile && (
            <Button
              className="chat-form-attach chat-form-attach-desktop"
              type="button"
              disabled={props.disabled || isUploading}
              onClick={() => fileInputRef.current?.click()}
              title={props.t("CHAT_FORM.add_file")}
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </Button>
          )}

          {/* Mobile dugme za file upload - bez resize funkcionalnosti */}
          {isMobile && (
            <Button
              className="chat-form-attach chat-form-attach-mobile"
              type="button"
              disabled={props.disabled || isUploading}
              onClick={() => fileInputRef.current?.click()}
              title={props.t("CHAT_FORM.add_file")}
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </Button>
          )}

          {/* Nevidljiv file input */}
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={handleFileSelect}
            accept="image/*,.pdf,.doc,.docx,.txt"
          />

          <Button
            className="chat-form-submit"
            type="submit"
            disabled={props.disabled || isUploading}
          >
            <FontAwesomeIcon icon={faCaretRight} size="2x" />
          </Button>
        </AvForm>

        <small className="ctrl-enter-hint">Ctrl + Enter za slanje</small>
      </div>
      <div className="chat-powered-by text-center">
        Complete Inbox by{" "}
        <a
          href="https://complete-inbox.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="chat-powered-link"
        >
          Firebird
        </a>
      </div>
    </>
  );
};

export default withTranslation()(ChatForm);
